import React from 'react';
import clsx from 'clsx';
import { formatMetaInsightsValue } from '@/utils/currency';

/** Major-unit amount in the given currency ("AED 45.00"). */
export function formatMoney(amount: number | null | undefined, currency: string): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return formatMetaInsightsValue(String(amount), currency);
}

export const Switch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'md';
}> = ({ checked, onChange, disabled, label, size = 'md' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
    className={clsx(
      'relative shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50',
      size === 'sm' ? 'h-[18px] w-8' : 'h-[22px] w-10',
      checked ? 'bg-success' : 'bg-border-strong',
    )}
  >
    <span className={clsx(
      'absolute rounded-full bg-white shadow-soft transition-all',
      size === 'sm' ? 'top-[2px] h-[14px] w-[14px]' : 'top-[3px] h-4 w-4',
      checked ? (size === 'sm' ? 'left-[16px]' : 'left-[21px]') : (size === 'sm' ? 'left-[2px]' : 'left-[3px]'),
    )} />
  </button>
);

export function Segmented<T extends string>({ value, onChange, options, className, size = 'md' }: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string; icon?: React.ReactNode }>;
  className?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div role="radiogroup" className={clsx('inline-flex rounded-xl border border-border bg-surface-alt p-1', className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={clsx(
              'inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-all',
              size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-[13px]',
              on ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Rounded filter chip used in the catalog and order filter bars. */
export const Chip: React.FC<{ active?: boolean; onClick?: () => void; children: React.ReactNode; count?: number }> = ({ active, onClick, children, count }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={clsx(
      'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12.5px] font-medium transition-colors',
      active ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:border-border-strong hover:text-text-primary',
    )}
  >
    {children}
    {count !== undefined && (
      <span className={clsx('rounded-full px-1.5 text-2xs tabular-nums', active ? 'bg-primary/15' : 'bg-surface-alt text-text-muted')}>{count}</span>
    )}
  </button>
);
